import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { Calendar } from 'lucide-react';
import { Helmet } from 'react-helmet-async';

interface Release {
  version: string;
  date: string;
  title: string;
  features: {
    [key: string]: string;
  };
}

function ReleaseNotes() {
  const { t } = useTranslation('release-notes');
  const releases = t('releases', { returnObjects: true }) as Release[];
  const { lang } = useParams<{ lang: string }>();

  return (
    <>
      <Helmet>
        <title>Release Notes - ShiftPic</title>
        <meta name="description" content="Stay up to date with the latest improvements and features added to ShiftPic. Our release notes detail all updates and enhancements to the image converter." />
        <meta name="keywords" content="ShiftPic updates, release notes, new features, image converter updates, changelog, version history, software updates, image converter features, ShiftPic improvements, conversion tool updates" />
        <link rel="canonical" href={`https://shiftpic.com/${lang || 'en'}/release-notes/`} />
        <meta property="og:title" content="Release Notes - ShiftPic" />
        <meta property="og:description" content="Stay up to date with the latest improvements and features added to ShiftPic. Our release notes detail all updates and enhancements to the image converter." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`https://shiftpic.com/${lang || 'en'}/release-notes/`} />
        <meta property="og:image" content="https://shiftpic.com/og-image.jpg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Release Notes - ShiftPic" />
        <meta name="twitter:description" content="Stay up to date with the latest improvements and features added to ShiftPic. Our release notes detail all updates and enhancements to the image converter." />
        <meta name="twitter:image" content="https://shiftpic.com/og-image.jpg" />
      </Helmet>

      <div className="max-w-4xl mx-auto px-4 py-12">
          <div className="mt-8 mb-16">
            <h1>{t('title')}</h1>
            <p>{t('description')}</p>
          </div>

          <div className="space-y-8">
            {releases.map((release) => (
              <section 
                key={release.version}
                className="card p-8" 
                aria-labelledby={`release-${release.version}`}
              >
                <div className="flex flex-col md:flex-row justify-between items-center mb-6">
                  <h3 id={`release-${release.version}`}>
                    {release.title}, v{release.version}
                  </h3>
                  <div className="flex items-center text-sm text-gray-700">
                    <Calendar size={16} className="mr-1" aria-hidden="true" />
                    <time dateTime={release.date}>{release.date}</time>
                  </div>
                </div>
                <div>
                  <ul className="list-disc pl-4">
                    {Object.entries(release.features).map(([, feature], index) => (
                      <li key={index}>{feature}</li>
                    ))}
                  </ul>
                </div>
              </section>
            ))}
          </div>
        </div>
    </>
  );
}

export default ReleaseNotes;